import NavbarWebsite from "../components/NavbarWebsite";
import womanbUrl from "../assets/img/woman.png";
import darkflowersUrl from "../assets/img/darkflowers.png";
import NavbarFooter from "../components/Footer";

import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/about")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <div>
        <NavbarWebsite />
      </div>

      <div className="max-w-3xl mx-auto md:mt-16 mt-10">
        <p className="text-4xl font-extrabold text-center mb-4 pt-9 ">
          From a student idea to a language lab
        </p>
        <p className="text-l font-normal text-center mb-4 pb-2 px-4">
          A space where your interests shape your learning — not the other way
          around.{" "}
        </p>
      </div>

      <div className="px-2 md:pt-10 flex justify-center w-full">
        <div className="relative z-10 flex flex-col md:flex-row items-stretch w-full max-w-5xl bg-[#ffa77a] rounded-xl p-4 md:p-6 shadow-lg">
          {/* IMAGE */}
          <div className="shrink-0 mb-6 md:mb-0 md:mr-8 w-full md:w-auto">
            <img
              src={womanbUrl}
              alt="Learn together"
              className="md:max-w-none md:w-[260px] md:h-[380px] w-[100px] h-[180px] object-cover rounded-lg hidden md:block"
            />
          </div>

          {/* TEXT CONTENT */}
          <div className="flex-1 flex flex-col justify-between text-left md:h-[380px]">
            {/* Top Text */}
            <div>
              <p className="md:text-xl text-lg text-black pb-4">
                I was never really happy with how online language learning
                worked. Most platforms felt more like games than tools. Too much
                gamification, too many drag-and-drop exercises, too many clicks
                just to reach something meaningful.
              </p>
              <p className="md:text-xl text-lg text-black pb-4">
                It all felt a bit childish — more focused on keeping me
                entertained than actually helping me learn. And the content? It
                was rarely something I cared about. I wasn’t looking to practice
                restaurant phrases or earn a cheerful ding for clicking the
                right answer. I wanted to learn a language through ideas,
                stories, and subjects that genuinely mattered to me — in a calm,
                focused space.
              </p>
            </div>

            {/* Bottom Credit */}
            <div className="flex md:flex-col md:items-start mt-4">
              {/* Mobile version only: image + text side-by-side, bottom-aligned */}
              <div className="flex md:hidden flex-row items-end h-[130px] w-full">
                <img
                  src={womanbUrl}
                  alt="Learn together"
                  className="w-[90px] h-[130px] object-cover rounded-lg mr-3"
                />
                <div className="pb-1">
                  <p className="text-l font-extrabold text-black leading-none">
                    <span className="text-[#ff7ad5]">Andi</span>
                  </p>
                  <p className="text-sm text-neutral-600 font-light leading-none">
                    Creator
                  </p>
                </div>
              </div>

              {/* Desktop version only */}
              <div className="hidden md:block">
                <p className="text-base font-extrabold text-black pb-1 mt-2">
                  <span className="text-[#ff7ad5]">Andi</span>
                </p>
                <p className="text-sm text-neutral-600 font-light">Creator</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mx-4 bg-white text-left border-neutral-200 md:p-6 ">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto pb-5 pt-10 md:py-10">
          <div>
            <h2 className="text-3xl font-semibold text-neutral-900 mb-6">
              Where it all began
            </h2>
            <p className="text-lg leading-relaxed text-neutral-800 mb-6">
              Uxhulu began as a university project in 2018. The first prototype
              was simple — a tool that connected vocabulary with images as
              mnemonics. The idea was to make hard words stick by pairing them
              with visuals you wouldn’t forget.
            </p>
            <p className="text-lg leading-relaxed text-neutral-800 mb-6">
              Since then, it’s grown into a flexible learning system — one that
              helps you learn through what truly matters to you. Today, you
              can:{" "}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pb-16">
          <div className="p-4 bg-[#fefaa7] h-full flex items-center justify-center">
            <p className="text-sm font-medium">Generate personalized texts</p>
          </div>
          <div className="p-4 bg-[#00edc8] h-full flex items-center justify-center">
            <p className="text-sm font-medium">Listen & practice with audio</p>
          </div>
          <div className="p-4 bg-[#ff7ad5] h-full flex items-center justify-center">
            <p className="text-sm font-medium">
              Upload images to create content
            </p>
          </div>
          <div className="p-4 bg-[#7a7eef] h-full flex items-center justify-center">
            <p className="text-sm font-medium">
              Use a personalized vocab trainer
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto text-center max-w-2xl px-4">
        <h2 className="text-3xl font-semibold mb-6">
          Built from curiosity, not by a big team.
        </h2>
        <p className="text-lg text-neutral-700 mb-16">
          Uxhulu is an idea turned into a tool. Not by a team of engineers, but
          by one person with a clear goal: to create a space for learning that
          respects your time, your interests, and your pace. No gamified
          distractions. No drag-and-drop drills. No quirky cartoons. Just clean,
          focused tools that help you create content you care about — and
          actually learn through it. It starts simple, and grows with you. Every
          suggestion and bit of feedback makes it better, shaped not by
          deadlines but by real learners who want something deeper. Like you.
        </p>
      </section>

      <section className="mx-auto text-center max-w-2xl px-4">
        <h2 className="text-3xl font-semibold mb-6">Who this is for</h2>
        <p className="text-lg text-neutral-700 mb-12">
          Whether you're just starting out or returning after years, this is a
          space to learn a language at your own pace, using content shaped by
          your own ideas.
        </p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-2 text-left pb-20 px-4">
        <div className="rounded-xs bg-[#ffa77a] p-8">
          <h3 className="text-xl font-bold mb-2">Clarity First</h3>
          <p className="text-sm text-neutral-700">
            A clean, focused experience. No bells and whistles — just progress.
          </p>
        </div>

        <div className="rounded-xs bg-[#ffa77a] p-8">
          <h3 className="text-xl font-bold mb-2">Learn What You Love</h3>
          <p className="text-sm text-neutral-700">
            Create content around your curiosity — that’s where learning sticks.
          </p>
        </div>

        <div className="rounded-xs bg-[#ffa77a] p-8">
          <h3 className="text-xl font-bold mb-2">Built for Humans</h3>
          <p className="text-sm text-neutral-700">
            No pressure, no games — just a calm, creative space to grow.
          </p>
        </div>
      </section>

      <section className="mx-auto text-center max-w-2xl px-4">
        <h2 className="text-3xl font-semibold mb-6">
          Still just the beginning
        </h2>
        <p className="text-lg text-neutral-700 mb-6">
          I’m constantly evolving this tool — testing new ideas, refining what
          works, and listening to real learners like you. There’s more to come,
          but the heart of it stays the same: learning that fits you.
        </p>
        <p className="text-lg font-medium">
          Start small. Create a text. Upload a photo. Learn something
          meaningful.
        </p>
      </section>

      <div className="w-full relative pb-8 pt-16">
        <img
          src={darkflowersUrl}
          alt="Dark flowers"
          className="w-full h-auto max-w-full"
          style={{ height: "530px" }}
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h2 className="text-4xl font-semibold text-white  mb-4rounded-lg px-4 py-2">
            Make learning feel like yours again.
          </h2>
          <p className="text-xl text-white max-w-2xl mx-auto mb-4 rounded-lg px-4 py-2">
            If you’ve ever opened a online learning application and felt
            overwhelmed, annoyed, or just plain bored — I get it. That’s why I
            built this: for people who want more depth, more freedom, and less
            noise.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center mt-6">
            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
              <div className=" rounded-xl w-full max-w-2xl h-32"></div>
            </div>

            <Link
              to="/pricing"
              className=" text-white outline-solid outline-1 outline-white backdrop-blur-md bg-white/30 font-semibold px-4 py-2 rounded-md shadow-sm hover:bg-white/50 transition"
            >
              View pricing
            </Link>
            <Link
              to="/free_trial_signup"
              className=" text-black backdrop-blur-md bg-white font-semibold px-4 py-2 rounded-md shadow-sm hover:bg-white/50 transition"
            >
              Start free trial
            </Link>
          </div>
        </div>
      </div>
      <NavbarFooter />
    </>
  );
}
